import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import { useMotionSafe } from "../../../hooks/useMotionSafe";
import { cn } from "../cn";

export interface GroupedTab<Id extends string> {
    id: Id;
    label: string;
    /** A rendered icon element. Decorative — the label carries the meaning. */
    icon: React.ReactNode;
}

export interface TabGroup<Id extends string> {
    id: string;
    label: string;
    icon: React.ReactNode;
    tabs: GroupedTab<Id>[];
}

interface GroupedTabNavProps<Id extends string> {
    /** Accessible name for the tab list, e.g. the patient's name. */
    label: string;
    /** id of the panel these tabs control. */
    panelId: string;
    activeTab: Id;
    onChange: (id: Id) => void;
    /** Tabs shown before the groups. */
    leadingTabs: GroupedTab<Id>[];
    /** Collapsed groups of related tabs. */
    groups: TabGroup<Id>[];
    /** Tabs shown after the groups. */
    trailingTabs?: GroupedTab<Id>[];
    /** Distinguishes the sliding indicator when two navs share a page. */
    layoutId?: string;
}

/**
 * Tab navigation for a section with more tabs than a row can hold, where some
 * of them collapse into named groups.
 *
 * The patient and couple-patient views both carry ten tabs with two dropdown
 * groups, and both had inlined the same ~200 lines of markup to draw it. Neither
 * copy carried any tab semantics: they were plain buttons, so a screen reader
 * announced ten unrelated controls and no indication of which one was showing,
 * and the group triggers never said they opened anything.
 *
 * ARIA mapping, since grouped tabs are not a standard pattern:
 *  - the row is a `tablist`;
 *  - a plain tab is a `tab` that controls the panel;
 *  - a group trigger is a `tab` as well — it is selected when one of its
 *    children is active — carrying `aria-haspopup` and `aria-expanded`;
 *  - the items inside a group are `menuitemradio`, which is the right role for
 *    a set of mutually exclusive choices.
 */
export default function GroupedTabNav<Id extends string>({
    label,
    panelId,
    activeTab,
    onChange,
    leadingTabs,
    groups,
    trailingTabs = [],
    layoutId = "grouped-tab-indicator",
}: GroupedTabNavProps<Id>) {
    const { breakpoint } = useBreakpoint();
    const { reduced } = useMotionSafe();
    const isMobileView = breakpoint === "mobile";

    const [openGroup, setOpenGroup] = useState<string | null>(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const rowRefs = useRef<Record<string, HTMLButtonElement | null>>({});

    const allTabs = [...leadingTabs, ...groups.flatMap((g) => g.tabs), ...trailingTabs];
    const active = allTabs.find((tab) => tab.id === activeTab) ?? allTabs[0];

    /** Row order for arrow-key navigation: plain tabs and group triggers alike. */
    const rowKeys = [
        ...leadingTabs.map((t) => t.id as string),
        ...groups.map((g) => g.id),
        ...trailingTabs.map((t) => t.id as string),
    ];

    const groupOf = (id: Id) => groups.find((g) => g.tabs.some((t) => t.id === id));
    const activeRowKey = groupOf(activeTab)?.id ?? (activeTab as string);

    // Escape closes an open group wherever focus happens to be.
    useEffect(() => {
        if (openGroup === null) return;
        const onKey = (event: KeyboardEvent) => {
            if (event.key !== "Escape") return;
            rowRefs.current[openGroup]?.focus();
            setOpenGroup(null);
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [openGroup]);

    if (!active) return null;

    const moveFocus = (event: React.KeyboardEvent, currentKey: string) => {
        const offset = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
        if (offset === 0) return;
        event.preventDefault();
        const index = rowKeys.indexOf(currentKey);
        const next = rowKeys[(index + offset + rowKeys.length) % rowKeys.length];
        rowRefs.current[next]?.focus();
    };

    const select = (id: Id) => {
        onChange(id);
        setOpenGroup(null);
        setMobileOpen(false);
    };

    const indicator = (
        <motion.span
            layoutId={reduced ? undefined : layoutId}
            aria-hidden="true"
            className="absolute inset-0 rounded-md bg-accent-tint"
            transition={{ duration: 0.24, ease: [0.2, 0, 0, 1] }}
        />
    );

    const rowItemClass = (isActive: boolean) =>
        cn(
            "relative flex min-h-11 items-center gap-2 whitespace-nowrap rounded-md px-4 text-sm font-semibold",
            "transition-colors duration-[--duration-fast] ease-[--ease-standard]",
            isActive
                ? "text-accent-on-tint"
                : "text-content-secondary hover:text-content-primary"
        );

    if (isMobileView) {
        return (
            <div className="relative z-30 mb-6 w-full">
                <div className="relative mx-auto max-w-sm px-1">
                    <button
                        type="button"
                        aria-haspopup="menu"
                        aria-expanded={mobileOpen}
                        aria-label={label}
                        onClick={() => setMobileOpen((v) => !v)}
                        className="flex h-12 w-full items-center justify-between rounded-lg border border-border-subtle bg-surface-raised px-4 transition-colors duration-[--duration-fast]"
                    >
                        <span className="flex items-center gap-3">
                            <span aria-hidden="true" className="text-accent-text">
                                {active.icon}
                            </span>
                            <span className="text-sm font-semibold text-content-primary">
                                {active.label}
                            </span>
                        </span>
                        <ChevronDown
                            aria-hidden="true"
                            className={cn(
                                "size-5 text-content-secondary transition-transform duration-[--duration-base]",
                                mobileOpen && "rotate-180"
                            )}
                        />
                    </button>

                    <AnimatePresence>
                        {mobileOpen && (
                            <>
                                <div
                                    className="fixed inset-0 z-40"
                                    onClick={() => setMobileOpen(false)}
                                />
                                <motion.div
                                    role="menu"
                                    aria-label={label}
                                    initial={{ opacity: 0, y: reduced ? 0 : -8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: reduced ? 0 : -8 }}
                                    transition={{ duration: reduced ? 0.12 : 0.2 }}
                                    className="custom-scrollbar absolute left-0 right-0 z-50 mt-2 max-h-[60vh] overflow-y-auto rounded-lg border border-border-subtle bg-surface-overlay p-1.5 shadow-overlay"
                                >
                                    {leadingTabs.map((tab) => (
                                        <MobileItem
                                            key={tab.id}
                                            tab={tab}
                                            active={tab.id === activeTab}
                                            onSelect={select}
                                        />
                                    ))}
                                    {groups.map((group) => (
                                        <div key={group.id} className="mt-1 first:mt-0">
                                            <p className="px-4 pb-1 pt-2 text-xs font-semibold uppercase tracking-[0.08em] text-content-secondary">
                                                {group.label}
                                            </p>
                                            {group.tabs.map((tab) => (
                                                <MobileItem
                                                    key={tab.id}
                                                    tab={tab}
                                                    active={tab.id === activeTab}
                                                    onSelect={select}
                                                />
                                            ))}
                                        </div>
                                    ))}
                                    {trailingTabs.map((tab) => (
                                        <MobileItem
                                            key={tab.id}
                                            tab={tab}
                                            active={tab.id === activeTab}
                                            onSelect={select}
                                        />
                                    ))}
                                </motion.div>
                            </>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        );
    }

    const plainTab = (tab: GroupedTab<Id>) => {
        const isActive = tab.id === activeTab;
        return (
            <button
                key={tab.id}
                type="button"
                role="tab"
                ref={(node) => {
                    rowRefs.current[tab.id as string] = node;
                }}
                aria-selected={isActive}
                aria-controls={panelId}
                tabIndex={activeRowKey === tab.id ? 0 : -1}
                onKeyDown={(event) => moveFocus(event, tab.id as string)}
                onClick={() => select(tab.id)}
                className={rowItemClass(isActive)}
            >
                {isActive && indicator}
                <span aria-hidden="true" className="relative z-10">
                    {tab.icon}
                </span>
                <span className="relative z-10">{tab.label}</span>
            </button>
        );
    };

    return (
        <div className="relative z-30 mb-6 w-full">
            <div className="flex justify-start px-1 pb-1 sm:justify-center">
                <div
                    role="tablist"
                    aria-label={label}
                    className="mx-auto inline-flex flex-wrap items-center justify-center gap-y-1 rounded-lg border border-border-subtle bg-surface-sunken p-1 sm:mx-0"
                >
                    {leadingTabs.map(plainTab)}

                    {groups.map((group) => {
                        const isActive = group.tabs.some((tab) => tab.id === activeTab);
                        const isOpen = openGroup === group.id;
                        return (
                            <div key={group.id} className="relative">
                                <button
                                    type="button"
                                    role="tab"
                                    ref={(node) => {
                                        rowRefs.current[group.id] = node;
                                    }}
                                    aria-selected={isActive}
                                    aria-controls={panelId}
                                    aria-haspopup="menu"
                                    aria-expanded={isOpen}
                                    tabIndex={activeRowKey === group.id ? 0 : -1}
                                    onKeyDown={(event) => {
                                        if (event.key === "ArrowDown") {
                                            event.preventDefault();
                                            setOpenGroup(group.id);
                                            return;
                                        }
                                        moveFocus(event, group.id);
                                    }}
                                    onClick={() => setOpenGroup(isOpen ? null : group.id)}
                                    className={rowItemClass(isActive)}
                                >
                                    {isActive && indicator}
                                    <span aria-hidden="true" className="relative z-10">
                                        {group.icon}
                                    </span>
                                    <span className="relative z-10">{group.label}</span>
                                    <ChevronDown
                                        aria-hidden="true"
                                        className={cn(
                                            "relative z-10 size-3.5 transition-transform duration-[--duration-base]",
                                            isOpen && "rotate-180"
                                        )}
                                    />
                                </button>

                                <AnimatePresence>
                                    {isOpen && (
                                        <>
                                            <div
                                                className="fixed inset-0 z-40"
                                                onClick={() => setOpenGroup(null)}
                                            />
                                            <motion.div
                                                role="menu"
                                                aria-label={group.label}
                                                initial={{ opacity: 0, y: reduced ? 0 : -6 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: reduced ? 0 : -6 }}
                                                transition={{ duration: reduced ? 0.12 : 0.15 }}
                                                className="absolute left-1/2 top-full z-50 mt-2 min-w-[11rem] -translate-x-1/2 space-y-0.5 rounded-lg border border-border-subtle bg-surface-overlay p-1.5 shadow-overlay"
                                            >
                                                {group.tabs.map((tab) => {
                                                    const tabActive = tab.id === activeTab;
                                                    return (
                                                        <button
                                                            key={tab.id}
                                                            type="button"
                                                            role="menuitemradio"
                                                            aria-checked={tabActive}
                                                            onClick={() => select(tab.id)}
                                                            className={cn(
                                                                "flex min-h-11 w-full items-center gap-3 whitespace-nowrap rounded-md px-4 text-sm font-medium",
                                                                "transition-colors duration-[--duration-fast]",
                                                                tabActive
                                                                    ? "bg-accent-tint font-semibold text-accent-on-tint"
                                                                    : "text-content-secondary hover:bg-surface-sunken hover:text-content-primary"
                                                            )}
                                                        >
                                                            <span aria-hidden="true">{tab.icon}</span>
                                                            <span className="flex-1 text-left">
                                                                {tab.label}
                                                            </span>
                                                            {tabActive && (
                                                                <Check
                                                                    aria-hidden="true"
                                                                    className="size-4"
                                                                />
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </motion.div>
                                        </>
                                    )}
                                </AnimatePresence>
                            </div>
                        );
                    })}

                    {trailingTabs.map(plainTab)}
                </div>
            </div>
        </div>
    );
}

function MobileItem<Id extends string>({
    tab,
    active,
    onSelect,
}: {
    tab: GroupedTab<Id>;
    active: boolean;
    onSelect: (id: Id) => void;
}) {
    return (
        <button
            type="button"
            role="menuitemradio"
            aria-checked={active}
            onClick={() => onSelect(tab.id)}
            className={cn(
                "flex min-h-11 w-full items-center justify-between rounded-md px-4 py-2.5",
                "transition-colors duration-[--duration-fast]",
                active
                    ? "bg-accent-tint font-semibold text-accent-on-tint"
                    : "font-medium text-content-secondary hover:bg-surface-sunken hover:text-content-primary"
            )}
        >
            <span className="flex items-center gap-3">
                <span aria-hidden="true">{tab.icon}</span>
                <span className="text-sm">{tab.label}</span>
            </span>
            {active && <Check aria-hidden="true" className="size-4" />}
        </button>
    );
}
